import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["controlId", "error", "readOnly", "required"];
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FormFieldContext } from './FormFieldContext';
import { logDeprecation } from '../util/logging'; // This is currently only used to prevent non-native attributes from going to the DOM

var StyledFormControl = /*#__PURE__*/styled.input.withConfig({
  displayName: "StyledFormControl",
  componentId: "c11n-8-64-0__sc-18qgis1-0"
})([""]);
var FormControl = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var controlId = _ref.controlId,
      error = _ref.error,
      readOnly = _ref.readOnly,
      required = _ref.required,
      rest = _objectWithoutProperties(_ref, _excluded);

  var context = React.useContext(FormFieldContext);

  if (typeof controlId !== 'undefined') {
    logDeprecation("`controlId` should not be used on form controls, instead use the native `id` attribute");
  }

  return /*#__PURE__*/React.createElement(StyledFormControl, _extends({
    ref: ref,
    id: typeof controlId !== 'undefined' ? controlId : context.controlId,
    "aria-invalid": typeof error !== 'undefined' ? error : context.error,
    "aria-describedby": context.descriptionId,
    disabled: context.disabled,
    required: typeof required !== 'undefined' ? required : context.required,
    "aria-required": typeof required !== 'undefined' ? required : context.required,
    readOnly: typeof readOnly !== 'undefined' ? readOnly : context.readOnly
  }, rest));
});
FormControl.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Indicate that the value entered is invalid using the native
   * [`aria-invalid`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-invalid_attribute)
   * attribute.
   * This will be set automatically by the `error` prop.
   */
  'aria-invalid': PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  /**
   * If there are multiple labels for a control, it is best to set the native
   * [`aria-describedby`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-describedby_attribute)
   * with each of the label ids. You can read more [here](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-describedby_attribute).
   * Can be controlled by [`FormField`](#/Form%20components?id=formfield) context.
   */
  'aria-describedby': PropTypes.string,

  /**
   * @deprecated Use the native `id` attributed instead.
   */
  'controlId': PropTypes.string,

  /**
   * Signals that there is an error with the value and sets the
   * [aria-invalid](https://www.w3.org/TR/wai-aria/#aria-invalid) property.
   */
  'error': PropTypes.bool,

  /**
   * The native [`id`](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id)
   * attribute.
   * Can be controlled by [`FormField`](#/Form%20components?id=formfield) context.
   */
  'id': PropTypes.string,

  /**
   * Mark the control as required with the native [`required`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/required)
   * attribute.
   * Can be controlled by [`FormField`](#/Form%20components?id=formfield) context.
   */
  'required': PropTypes.bool,

  /**
   * Mark the control as disabled with the native [`disabled`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/disabled)
   * attribute.
   * Can be controlled by [`FormField`](#/Form%20components?id=formfield) context.
   */
  'disabled': PropTypes.bool,

  /**
   * Mark the input as read-only with the native [`readonly`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly)
   * attribute.
   * Can be controlled by [`FormField`](#/Form%20components?id=formfield) context.
   *
   * @since 8.27.0
   */
  'readOnly': PropTypes.bool
} : {};
export default FormControl;